import React, { useEffect } from "react";
import ReactDOM from "react-dom";

/**
 * Reusable portal modal.
 *
 * Props:
 *   show        {boolean}  — controls visibility
 *   onHide      {function} — called when overlay or close button is clicked
 *   title       {string}   — heading rendered inside the modal (legacy, used when no header/body/footer)
 *   maxWidth    {number}   — max-width of the modal panel in px (default 780)
 *   header      {node}     — content for app-modal__header section
 *   body        {node}     — content for app-modal__body section
 *   footer      {node}     — content for app-modal__footer section
 *   children    {node}     — fallback body content when header/body/footer not used
 *   locked      {boolean}  — prevents closing (no close btn, no overlay click, no Escape)
 *   closeOnOverlayClick {boolean} — close when the overlay is clicked (default true)
 *   blur        {boolean}  — blurs content behind overlay
 *   drawerRight {boolean}  — renders as a right-side slide-in drawer
 */
const AppModal = ({ show, onHide, title, maxWidth = 780, className, children, header, body, footer, locked = false, closeOnOverlayClick = true, blur = false, drawerRight = false }) => {
	// Close on Escape key
	useEffect(() => {
		if (!show || locked) return;
		const handler = (e) => { if (e.key === "Escape") onHide(); };
		document.addEventListener("keydown", handler);
		return () => document.removeEventListener("keydown", handler);
	}, [show, onHide, locked]);

	// Prevent body scroll while open
	useEffect(() => {
		if (show) {
			document.body.style.overflow = "hidden";
		} else {
			document.body.style.overflow = "";
		}
		return () => { document.body.style.overflow = ""; };
	}, [show]);

	if (!show) return null;

	return ReactDOM.createPortal(
		<div className={`app-modal-overlay${blur ? " app-modal-overlay--blur" : ""}${drawerRight ? " app-modal-overlay--right" : ""}`} onClick={locked || !closeOnOverlayClick ? undefined : onHide}>
			<div
				className={`app-modal${drawerRight ? " app-modal--drawer" : ""}${className ? ` ${className}` : ""}`}
				style={drawerRight ? undefined : { maxWidth }}
				onClick={(e) => e.stopPropagation()}
				role="dialog"
				aria-modal="true"
				aria-label={title}>
				<button
					className="app-modal-close"
					onClick={onHide}
					aria-label="Close">
					<svg width="16" height="16" viewBox="0 0 16 16" fill="none">
						<line x1="1" y1="1" x2="15" y2="15" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
						<line x1="15" y1="1" x2="1" y2="15" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
					</svg>
				</button>
				{(header !== undefined || body !== undefined || footer !== undefined) ? (
					<>
						{(header !== undefined || title) && (
							<div className="app-modal__header">{header ?? <h2 className="app-modal-title">{title}</h2>}</div>
						)}
						{body !== undefined && <div className="app-modal__body">{body}</div>}
						{footer !== undefined && <div className="app-modal__footer">{footer}</div>}
					</>
				) : (
					<>
						{title && <h2 className="app-modal-title">{title}</h2>}
						<div className="app-modal-body">{children}</div>
					</>
				)}
			</div>
		</div>,
		document.body
	);
};

export default AppModal;
